import React, { createContext, useCallback, useContext, useState } from 'react';

type FormDisplayProps = {
  accountName?: string;
  transferId?: string;
  forgotPassword?: boolean;
  ssoLogin?: boolean;
  updateTransferId?: (input: string) => void;
  updateCoreAccount?: (input: string) => void;
  updateForgotPassword?: (input: boolean) => void;
  updateSsoLogin?: (input: boolean) => void;
};
type FormDisplayProviderProps = {
  children: React.ReactNode;
  values: FormDisplayProps;
};

const FormDisplayContext = createContext<FormDisplayProps | undefined>(undefined);
const FormDisplayProvider = ({ children, values }: FormDisplayProviderProps) => {
  const [accountName, setAccountName] = useState(values?.accountName);
  const [transferId, setTransferId] = useState(values?.transferId);
  const [forgotPassword, setForgotPassword] = useState(values.forgotPassword ?? false);
  const [ssoLogin, setSsoLogin] = useState(values.ssoLogin ?? false);

  const updateTransferId = useCallback(
    input => {
      setTransferId(input);
    },
    [setTransferId]
  );
  const updateCoreAccount = useCallback(
    input => {
      setAccountName(input);
    },
    [setAccountName]
  );
  const updateForgotPassword = useCallback(
    input => {
      setForgotPassword(input);
    },
    [setForgotPassword]
  );
  const updateSsoLogin = useCallback(
    input => {
      setSsoLogin(input);
    },
    [setSsoLogin]
  );

  return (
    <FormDisplayContext.Provider
      value={{
        accountName,
        transferId,
        forgotPassword,
        ssoLogin,
        updateCoreAccount: values.updateCoreAccount ?? updateCoreAccount,
        updateTransferId: values.updateTransferId ?? updateTransferId,
        updateForgotPassword: values.updateForgotPassword ?? updateForgotPassword,
        updateSsoLogin: values.updateSsoLogin ?? updateSsoLogin
      }}
    >
      {children}
    </FormDisplayContext.Provider>
  );
};

const useCoreAccount = () => {
  const context = useContext(FormDisplayContext);

  if (context === undefined) {
    throw new Error('useCoreAccount must be called within a provider.');
  }

  const { accountName, updateCoreAccount } = context;
  return { accountName, updateCoreAccount };
};

const useTransferId = () => {
  const context = useContext(FormDisplayContext);

  if (context === undefined) {
    throw new Error('useTransferId must be called within a provider.');
  }

  const { transferId, updateTransferId } = context;
  return { transferId, updateTransferId };
};

const useSsoLogin = () => {
  const context = useContext(FormDisplayContext);

  if (context === undefined) {
    throw new Error('useSsoLogin must be called within a provider.');
  }

  const { ssoLogin, updateSsoLogin } = context;
  return { ssoLogin, updateSsoLogin };
};

const useForgotPassword = () => {
  const context = useContext(FormDisplayContext);

  if (context === undefined) {
    throw new Error('useForgotPassword must be called within a provider.');
  }

  const { forgotPassword, updateForgotPassword } = context;
  return { forgotPassword, updateForgotPassword };
};

export { FormDisplayProvider, useCoreAccount, useSsoLogin, useTransferId, useForgotPassword };
